import { useContext, useEffect, useState } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "react-oidc-context";

// Project modules
import { GameContext } from "./Context/GameContext";
import { isLocalhost } from "./Config/config";

// Local pages
import RefreshSettings from "./RefreshSettings";
import EventManagement from "./Routes/Events/EventManagement";
import EscapeRoom from "./Routes/EscapeRoom/EscapeRoom";
import SinglePlaythrough from "./Routes/SinglePlaythrough/SinglePlaythrough";
import LeaderboardPage from "./Routes/Leaderboard/LeaderboardPage";
import Trapped from "./Routes/Trapped/Trapped";
import Winner from "./Routes/Winner/Winner";
import Cheater from "./Routes/Cheater/Cheater";

// Side-effect imports (styles/fonts)
import "@fontsource/open-sans";
import "@fontsource/open-sans/400.css";
import "@fontsource/open-sans/400-italic.css";
import "./App.css";

/**
 * App
 * ---
 * Root application shell: initialises auth, decides admin capabilities, and mounts routes.
 * Behaviour:
 * - If running on localhost, bypass OIDC and grant admin privileges.
 * - Otherwise, trigger OIDC sign-in when unauthenticated; derive admin role from groups.
 * - Routes include the game, admin event management, leaderboard, and outcome screens.
 */

// Small, safe helper to read Cognito groups as string[]
function getCognitoGroups(profile: unknown): string[] {
  // profile is often a plain object with string-indexed claims
  const groups = (profile as Record<string, unknown> | undefined)?.["cognito:groups"];
  return Array.isArray(groups) ? (groups as string[]) : [];
}

const App = () => {
  /** CONTEXT ------------------------------------------------------- */
  const { isWinnerAccessible } = useContext(GameContext);

  /** AUTH & STATE -------------------------------------------------- */
  const [isUserAdmin, setIsUserAdmin] = useState<boolean>(false);
  const auth = useAuth();

  /** EFFECTS ------------------------------------------------------- */
  useEffect(() => {
    if (isLocalhost) {
      setIsUserAdmin(true);
      return;
    }

    if (!auth.isAuthenticated && !auth.isLoading) {
      auth.signinRedirect();
    } else if (auth.isAuthenticated) {
      const roles = getCognitoGroups(auth.user?.profile);
      setIsUserAdmin(roles.includes("admin"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.isLoading, isLocalhost]);

  /** RENDER GUARDS ------------------------------------------------- */
  if (!isLocalhost && auth.isLoading) {
    return <div>Loading...</div>;
  }

  if (auth.error) {
    return <div>Encountering error... {auth.error.message}</div>;
  }

  /** ROUTES RENDER ------------------------------------------------- */
  if (isLocalhost || auth.isAuthenticated) {
    return (
      <Router>
        <Routes>
          {/* Landing */}
          <Route path="/" element={<RefreshSettings admin={isUserAdmin} />} />

          {/* Settings and event management */}
          <Route path="/settings" element={<EventManagement admin={isUserAdmin} />} />

          {/* Game and outcomes */}
          <Route path="/escaperoom" element={<EscapeRoom />} />
          <Route
            path="/winner"
            element={isWinnerAccessible ? <Winner /> : <Navigate to="/cheater" />}
          />
          <Route path="/trapped" element={<Trapped />} />
          <Route path="/cheater" element={<Cheater />} />
          <Route path="/single-playthrough" element={<SinglePlaythrough />} />

          {/* Leaderboard */}
          <Route path="/leaderboard" element={<LeaderboardPage admin={isUserAdmin} />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    );
  }

  // If we reach here, OIDC flow will redirect; render nothing
  return null;
};

export default App;